// File path: src/services/queries/coinglass/coinglassQueries.ts

import type {
  QueryFunction,
  QueryFunctionContext,
} from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import type {
  FundingRateHistoryResponse,
  OpenInterestHistoryResponse,
  TokenHistoryResponse,
  WeightedFundingRateHistoryResponse,
  ETFInflowsResponse,
  ETFInflowChartResponse,
  BullMarketPeakIndicatorsResponse,
  AHR999ChartResponse,
} from "@/types/coinglass";
import type {
  FearGreedChartResponse,
  HolderSupplyChartResponse,
  HolderRealizedPriceChartResponse,
  GlobalM2ChartResponse,
  FearGreedQueryParams,
  HolderSupplyQueryParams,
  HolderRealizedPriceQueryParams,
  GlobalM2QueryParams,
  PuellMultipleChartResponse,
  StockToFlowChartResponse,
  PiCycleTopChartResponse,
  GoldenRatioMultiplierChartResponse,
  PuellMultipleQueryParams,
  StockToFlowQueryParams,
  PiCycleTopQueryParams,
  GoldenRatioMultiplierQueryParams,
  // New Indicators Response Types
  MovingAverage200WHeatmapChartResponse,
  AltcoinSeasonIndexChartResponse,
  BitcoinBubbleIndexChartResponse,
  BitcoinMacroOscillatorChartResponse,
  BitcoinRainbowChartResponse,
  BitcoinReserveRiskChartResponse,
  BitcoinRHODLRatioChartResponse,
  // New Indicators Query Params
  MovingAverage200WHeatmapQueryParams,
  AltcoinSeasonIndexQueryParams,
  BitcoinBubbleIndexQueryParams,
  BitcoinMacroOscillatorQueryParams,
  BitcoinRainbowChartQueryParams,
  BitcoinReserveRiskQueryParams,
  BitcoinRHODLRatioQueryParams,
} from "@/types/indices";
import { queryKeys } from "@/constants/queryKeys";
import { TIME } from "@/constants/app";
import { DATA_TYPE_CONFIG } from "@/constants/coinglass/coinglass";

// ... (existing imports and code)

/**
 * Fetch Puell Multiple Chart
 */
export const fetchPuellMultipleChart: QueryFunction<
  PuellMultipleChartResponse,
  ReturnType<typeof queryKeys.coinglass.puellMultiple.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/puell-multiple-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Puell Multiple Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch Puell Multiple Chart: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error("[API] Puell Multiple Chart Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Stock-to-Flow Chart
 */
export const fetchStockToFlowChart: QueryFunction<
  StockToFlowChartResponse,
  ReturnType<typeof queryKeys.coinglass.stockToFlow.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/stock-to-flow-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Stock-to-Flow Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch Stock-to-Flow Chart: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error("[API] Stock-to-Flow Chart Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Pi Cycle Top Chart
 */
export const fetchPiCycleTopChart: QueryFunction<
  PiCycleTopChartResponse,
  ReturnType<typeof queryKeys.coinglass.piCycleTop.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/pi-cycle-top-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Pi Cycle Top Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch Pi Cycle Top Chart: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error("[API] Pi Cycle Top Chart Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Golden Ratio Multiplier Chart
 */
export const fetchGoldenRatioMultiplierChart: QueryFunction<
  GoldenRatioMultiplierChartResponse,
  ReturnType<typeof queryKeys.coinglass.goldenRatioMultiplier.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/golden-ratio-multiplier-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Golden Ratio Multiplier Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    if (!response.ok) {
      throw new Error(`Failed to fetch Golden Ratio Multiplier Chart: ${response.status} ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    console.error("[API] Golden Ratio Multiplier Chart Fetch Error:", error);
    throw error;
  }
};

// ... (existing query options)

/**
 * Query options for Puell Multiple Chart
 */
export const puellMultipleChartQueryOptions = (params?: PuellMultipleQueryParams) => ({
  queryKey: queryKeys.coinglass.puellMultiple.chart(params),
  queryFn: fetchPuellMultipleChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Stock-to-Flow Chart
 */
export const stockToFlowChartQueryOptions = (params?: StockToFlowQueryParams) => ({
  queryKey: queryKeys.coinglass.stockToFlow.chart(params),
  queryFn: fetchStockToFlowChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Pi Cycle Top Chart
 */
export const piCycleTopChartQueryOptions = (params?: PiCycleTopQueryParams) => ({
  queryKey: queryKeys.coinglass.piCycleTop.chart(params),
  queryFn: fetchPiCycleTopChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Golden Ratio Multiplier Chart
 */
export const goldenRatioMultiplierChartQueryOptions = (params?: GoldenRatioMultiplierQueryParams) => ({
  queryKey: queryKeys.coinglass.goldenRatioMultiplier.chart(params),
  queryFn: fetchGoldenRatioMultiplierChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

// ============================================================================
// QUERY FUNCTIONS
// ============================================================================

/**
 * Fetch funding rate history for a ticker across multiple exchanges
 */
export const fetchFundingRateHistory: QueryFunction<
  FundingRateHistoryResponse,
  ReturnType<typeof queryKeys.coinglass.fundingRate.history>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const { ticker, exchanges } = params;

  const exchangeParams = exchanges.join(",");
  const url = `${API_BASE_URL}/coinglass/history/fr?symbols=${ticker}&exchanges=${exchangeParams}&format=close`;

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch funding rate: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Fetch token price history
 * Uses the new multi-ticker /tokens/history/points endpoint
 */
export const fetchTokenPriceHistory: QueryFunction<
  TokenHistoryResponse,
  ReturnType<typeof queryKeys.coinglass.tokenPrice.history>
> = async ({ queryKey, signal }) => {
  const [, , , ticker] = queryKey;

  const url = `${API_BASE_URL}/tokens/history/points?tickers=${ticker}&metric_keys=price_close`;

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch token price: ${response.status} ${response.statusText}`
    );
  }

  // Multi-ticker endpoint returns an array, extract the first (and only) result
  const results = (await response.json()) as TokenHistoryResponse[];
  if (!results || results.length === 0) {
    throw new Error(`No data returned for ticker ${ticker}`);
  }
  return results[0]!;
};

/**
 * Fetch open interest history for a ticker
 */
export const fetchOpenInterestHistory: QueryFunction<
  OpenInterestHistoryResponse,
  ReturnType<typeof queryKeys.coinglass.openInterest.history>
> = async ({ queryKey, signal }) => {
  const [, , , ticker] = queryKey;

  const url = `${API_BASE_URL}/coinglass/history/oi?symbols=${ticker}&format=close`;

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch open interest: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

// File path: src/api/coinglassQueries.ts

// Add this query function after fetchOpenInterestHistory:

/**
 * Fetches weighted funding rate history for a token
 */
const fetchWeightedFundingRateHistory = async ({
  queryKey,
}: QueryFunctionContext<
  ReturnType<typeof queryKeys.coinglass.weightedFundingRate.history>
>): Promise<WeightedFundingRateHistoryResponse> => {
  const [, , , ticker, dataType] = queryKey;

  // Map dataType to API endpoint
  const endpoint = DATA_TYPE_CONFIG[dataType].endpoint;

  const url = `${API_BASE_URL}${endpoint}?symbols=${ticker}&format=close`;
  const response = await fetch(url, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch weighted funding rate: ${response.statusText}`
    );
  }

  const data = await response.json();
  return data;
};

/**
 * Fetch ETF inflows history for a ticker
 */
export const fetchETFInflowsHistory: QueryFunction<
  ETFInflowsResponse,
  ReturnType<typeof queryKeys.coinglass.etfInflows.history>
> = async ({ queryKey, signal }) => {
  const [, , , ticker] = queryKey;

  const url = `${API_BASE_URL}/coinglass/history/etf-inflows?ticker=${ticker}`;

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ETF inflows: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Fetch ETF inflow chart data for a ticker
 */
export const fetchETFInflowChart: QueryFunction<
  ETFInflowChartResponse,
  ReturnType<typeof queryKeys.coinglass.etfInflows.chart>
> = async ({ queryKey, signal }) => {
  const [, , , ticker] = queryKey;

  const url = `${API_BASE_URL}/coinglass/history/etf-inflows-chart?ticker=${ticker}`;

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ETF inflow chart: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Fetch Fear and Greed Index chart data
 */
export const fetchFearGreedChart: QueryFunction<
  FearGreedChartResponse,
  ReturnType<typeof queryKeys.coinglass.fearGreed.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params.token_id) queryParams.append("token_id", params.token_id.toString());
  if (params.start_date) queryParams.append("start_date", params.start_date);
  if (params.end_date) queryParams.append("end_date", params.end_date);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/fear-greed-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Fear & Greed Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] Fear & Greed Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Fear & Greed chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log("[API] Fear & Greed Data received:", data ? "Yes" : "No");
    console.log("[API] Fear & Greed Data:", data);
    return data;
  } catch (error) {
    console.error("[API] Fear & Greed Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Bitcoin Holder Supply chart data
 */
export const fetchHolderSupplyChart: QueryFunction<
  HolderSupplyChartResponse,
  ReturnType<typeof queryKeys.coinglass.holderSupply.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params.token_id) queryParams.append("token_id", params.token_id.toString());
  if (params.start_date) queryParams.append("start_date", params.start_date);
  if (params.end_date) queryParams.append("end_date", params.end_date);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/holder-supply-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Holder Supply Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] Holder Supply Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Holder Supply chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log("[API] Holder Supply Data received:", data ? "Yes" : "No");
    return data;
  } catch (error) {
    console.error("[API] Holder Supply Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Bitcoin Holder Realized Price chart data
 */
export const fetchHolderRealizedPriceChart: QueryFunction<
  HolderRealizedPriceChartResponse,
  ReturnType<typeof queryKeys.coinglass.holderRealizedPrice.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params.token_id) queryParams.append("token_id", params.token_id.toString());
  if (params.start_date) queryParams.append("start_date", params.start_date);
  if (params.end_date) queryParams.append("end_date", params.end_date);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/holder-realized-price-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Holder Realized Price Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] Holder Realized Price Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Holder Realized Price chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log("[API] Holder Realized Price Data received:", data ? "Yes" : "No");
    return data;
  } catch (error) {
    console.error("[API] Holder Realized Price Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Bitcoin vs Global M2 chart data
 */
export const fetchGlobalM2Chart: QueryFunction<
  GlobalM2ChartResponse,
  ReturnType<typeof queryKeys.coinglass.globalM2.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params.token_id) queryParams.append("token_id", params.token_id.toString());
  if (params.start_date) queryParams.append("start_date", params.start_date);
  if (params.end_date) queryParams.append("end_date", params.end_date);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/global-m2-chart?${queryParams.toString()}`;
  console.log("[API] Fetching Global M2 Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] Global M2 Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Global M2 chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log("[API] Global M2 Data received:", data ? "Yes" : "No");
    console.log("[API] Global M2 Data structure:", {
      hasData: !!data?.data,
      hasXAxis: !!data?.data?.xAxis,
      hasSeries: !!data?.data?.series,
      xAxisLength: data?.data?.xAxis?.length,
      seriesCount: data?.data?.series?.length,
      fullData: data
    });
    return data;
  } catch (error) {
    console.error("[API] Global M2 Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch Bull Market Peak Indicators
 */
export const fetchBullMarketPeakIndicators: QueryFunction<
  BullMarketPeakIndicatorsResponse,
  ReturnType<typeof queryKeys.coinglass.bullMarketPeakIndicators.list>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.latest_only !== undefined)
    queryParams.append("latest_only", params.latest_only.toString());
  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/bull-market-peak-indicators?${queryParams.toString()}`;
  console.log("[API] Fetching Bull Market Indicators:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] Bull Market Indicators Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Bull Market Indicators: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("[API] Bull Market Indicators Fetch Error:", error);
    throw error;
  }
};

/**
 * Fetch AHR999 Historical Chart
 */
export const fetchAHR999Chart: QueryFunction<
  AHR999ChartResponse,
  ReturnType<typeof queryKeys.coinglass.ahr999Chart.data>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/ahr999-chart?${queryParams.toString()}`;
  console.log("[API] Fetching AHR999 Chart:", url);

  const init: RequestInit = {};
  if (signal) {
    init.signal = signal;
  }

  try {
    const response = await fetch(url, init);
    console.log("[API] AHR999 Chart Response Status:", response.status);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch AHR999 Chart: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("[API] AHR999 Chart Fetch Error:", error);
    throw error;
  }
};

// ============================================================================
// QUERY OPTIONS FACTORIES
// ============================================================================

/**
 * Query options for funding rate history
 */
export const fundingRateHistoryQueryOptions = (params: {
  ticker: string;
  exchanges: string[];
}) => ({
  queryKey: queryKeys.coinglass.fundingRate.history(params),
  queryFn: fetchFundingRateHistory,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for token price history
 */
export const tokenPriceHistoryQueryOptions = (params: { ticker: string }) => ({
  queryKey: queryKeys.coinglass.tokenPrice.history(params),
  queryFn: fetchTokenPriceHistory,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for open interest history
 */
export const openInterestHistoryQueryOptions = (params: {
  ticker: string;
}) => ({
  queryKey: queryKeys.coinglass.openInterest.history(params),
  queryFn: fetchOpenInterestHistory,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for weighted funding rate history
 */
export const weightedFundingRateHistoryQueryOptions = (params: {
  ticker: string;
  dataType: "oiw" | "volw";
}) => ({
  queryKey: queryKeys.coinglass.weightedFundingRate.history(params),
  queryFn: fetchWeightedFundingRateHistory,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for ETF inflows history
 */
export const etfInflowsHistoryQueryOptions = (params: { ticker: string }) => ({
  queryKey: queryKeys.coinglass.etfInflows.history(params),
  queryFn: fetchETFInflowsHistory,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for ETF inflow chart data
 */
export const etfInflowChartQueryOptions = (params: { ticker: string }) => ({
  queryKey: queryKeys.coinglass.etfInflows.chart(params),
  queryFn: fetchETFInflowChart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Fear and Greed Index chart
 */
export const fearGreedChartQueryOptions = (params?: FearGreedQueryParams) => ({
  queryKey: queryKeys.coinglass.fearGreed.chart(params),
  queryFn: fetchFearGreedChart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Bitcoin Holder Supply chart
 */
export const holderSupplyChartQueryOptions = (
  params?: HolderSupplyQueryParams
) => ({
  queryKey: queryKeys.coinglass.holderSupply.chart(params),
  queryFn: fetchHolderSupplyChart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Bitcoin Holder Realized Price chart
 */
export const holderRealizedPriceChartQueryOptions = (
  params?: HolderRealizedPriceQueryParams
) => ({
  queryKey: queryKeys.coinglass.holderRealizedPrice.chart(params),
  queryFn: fetchHolderRealizedPriceChart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Bitcoin vs Global M2 chart
 */
export const globalM2ChartQueryOptions = (
  params?: GlobalM2QueryParams
) => ({
  queryKey: queryKeys.coinglass.globalM2.chart(params),
  queryFn: fetchGlobalM2Chart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for Bull Market Peak Indicators
 */
export const bullMarketPeakIndicatorsQueryOptions = (params?: {
  latest_only?: boolean | undefined;
  start_date?: string | undefined;
  end_date?: string | undefined;
  limit?: number | undefined;
}) => ({
  queryKey: queryKeys.coinglass.bullMarketPeakIndicators.list(params),
  queryFn: fetchBullMarketPeakIndicators,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

/**
 * Query options for AHR999 Historical Chart
 */
export const ahr999ChartQueryOptions = (params?: {
  start_date?: string | undefined;
  end_date?: string | undefined;
  limit?: number | undefined;
}) => ({
  queryKey: queryKeys.coinglass.ahr999Chart.data(params),
  queryFn: fetchAHR999Chart,
  staleTime: 5 * TIME.MINUTE, // 5 minutes
  gcTime: 10 * TIME.MINUTE, // 10 minutes
  refetchOnWindowFocus: false,
  retry: 2,
});

// ============================================================================
// NEW INDICATORS FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch 200-Week Moving Average Heatmap
 */
export const fetchMovingAverage200WHeatmapChart: QueryFunction<
  MovingAverage200WHeatmapChartResponse,
  ReturnType<typeof queryKeys.coinglass.movingAverage200WHeatmap.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/moving-average-200w-heatmap-chart?${queryParams.toString()}`;
  
  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch 200W MA Heatmap: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Altcoin Season Index
 */
export const fetchAltcoinSeasonIndexChart: QueryFunction<
  AltcoinSeasonIndexChartResponse,
  ReturnType<typeof queryKeys.coinglass.altcoinSeasonIndex.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.include_marketcap !== undefined) queryParams.append("include_marketcap", params.include_marketcap.toString());

  const url = `${API_BASE_URL}/coinglass/history/altcoin-season-index-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Altcoin Season Index: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Bitcoin Bubble Index
 */
export const fetchBitcoinBubbleIndexChart: QueryFunction<
  BitcoinBubbleIndexChartResponse,
  ReturnType<typeof queryKeys.coinglass.bitcoinBubbleIndex.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_date) queryParams.append("start_date", params.start_date);
  if (params?.end_date) queryParams.append("end_date", params.end_date);
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  // Note: Bubble Index uses date strings, not timestamps
  const url = `${API_BASE_URL}/coinglass/history/bitcoin-bubble-index-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Bitcoin Bubble Index: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Bitcoin Macro Oscillator
 */
export const fetchBitcoinMacroOscillatorChart: QueryFunction<
  BitcoinMacroOscillatorChartResponse,
  ReturnType<typeof queryKeys.coinglass.bitcoinMacroOscillator.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/bitcoin-macro-oscillator-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Bitcoin Macro Oscillator: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Bitcoin Rainbow Chart
 */
export const fetchBitcoinRainbowChart: QueryFunction<
  BitcoinRainbowChartResponse,
  ReturnType<typeof queryKeys.coinglass.bitcoinRainbowChart.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/bitcoin-rainbow-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Bitcoin Rainbow Chart: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Bitcoin Reserve Risk
 */
export const fetchBitcoinReserveRiskChart: QueryFunction<
  BitcoinReserveRiskChartResponse,
  ReturnType<typeof queryKeys.coinglass.bitcoinReserveRisk.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/bitcoin-reserve-risk-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Bitcoin Reserve Risk: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch Bitcoin RHODL Ratio
 */
export const fetchBitcoinRHODLRatioChart: QueryFunction<
  BitcoinRHODLRatioChartResponse,
  ReturnType<typeof queryKeys.coinglass.bitcoinRHODLRatio.chart>
> = async ({ queryKey, signal }) => {
  const [, , , params] = queryKey;
  const queryParams = new URLSearchParams();

  if (params?.start_timestamp) queryParams.append("start_timestamp", params.start_timestamp.toString());
  if (params?.end_timestamp) queryParams.append("end_timestamp", params.end_timestamp.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const url = `${API_BASE_URL}/coinglass/history/bitcoin-rhodl-ratio-chart?${queryParams.toString()}`;

  const init: RequestInit = {};
  if (signal) init.signal = signal;

  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`Failed to fetch Bitcoin RHODL Ratio: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

// ============================================================================
// NEW INDICATORS QUERY OPTIONS
// ============================================================================

export const movingAverage200WHeatmapQueryOptions = (params?: MovingAverage200WHeatmapQueryParams) => ({
  queryKey: queryKeys.coinglass.movingAverage200WHeatmap.chart(params),
  queryFn: fetchMovingAverage200WHeatmapChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const altcoinSeasonIndexQueryOptions = (params?: AltcoinSeasonIndexQueryParams) => ({
  queryKey: queryKeys.coinglass.altcoinSeasonIndex.chart(params),
  queryFn: fetchAltcoinSeasonIndexChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const bitcoinBubbleIndexQueryOptions = (params?: BitcoinBubbleIndexQueryParams) => ({
  queryKey: queryKeys.coinglass.bitcoinBubbleIndex.chart(params),
  queryFn: fetchBitcoinBubbleIndexChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const bitcoinMacroOscillatorQueryOptions = (params?: BitcoinMacroOscillatorQueryParams) => ({
  queryKey: queryKeys.coinglass.bitcoinMacroOscillator.chart(params),
  queryFn: fetchBitcoinMacroOscillatorChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const bitcoinRainbowChartQueryOptions = (params?: BitcoinRainbowChartQueryParams) => ({
  queryKey: queryKeys.coinglass.bitcoinRainbowChart.chart(params),
  queryFn: fetchBitcoinRainbowChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const bitcoinReserveRiskQueryOptions = (params?: BitcoinReserveRiskQueryParams) => ({
  queryKey: queryKeys.coinglass.bitcoinReserveRisk.chart(params),
  queryFn: fetchBitcoinReserveRiskChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});

export const bitcoinRHODLRatioQueryOptions = (params?: BitcoinRHODLRatioQueryParams) => ({
  queryKey: queryKeys.coinglass.bitcoinRHODLRatio.chart(params),
  queryFn: fetchBitcoinRHODLRatioChart,
  staleTime: 5 * TIME.MINUTE,
  gcTime: 10 * TIME.MINUTE,
  refetchOnWindowFocus: false,
  retry: 2,
});
